// src/widgets/RecentLogsWidgets.jsx
//
// Tiga widget dalam satu file:
//
//   <RecentChangeLogsWidget filter={{ resource_type: 'User', limit: 10 }} />
//   — Audit trail terbaru (scope sesuai role aktor)
//
//   <RecentAuthLogsWidget filter={{ event_type: 'login_success', limit: 10 }} />
//   — Aktivitas login admin terbaru (scope sesuai role aktor)
//
//   <RecentSyncLogsWidget filter={{ status: 'error', limit: 10 }} />
//   — Histori sync ke radius server terbaru

import { Link } from 'react-router-dom';
import { History, ShieldAlert, RefreshCw } from 'lucide-react';
import Empty from './Empty';
import { Badge } from '../components/UI';
import useWidgetData from '../hooks/useWidgetData';

function fmtDT(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('id-ID', { hour12: false, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const thStyle = {
  padding: '4px 8px', textAlign: 'left',
  color: 'var(--text-muted)', fontWeight: 700,
  textTransform: 'uppercase', fontSize: 10, whiteSpace: 'nowrap',
};
const tdStyle = { padding: '5px 8px', whiteSpace: 'nowrap' };

function CardShell({ icon: Icon, iconColor, title, viewAllTo, loading, isEmpty, emptyMessage, children }) {
  return (
    <div style={{
      padding: 16, height: '100%', boxSizing: 'border-box',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', overflow: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon size={13} color={iconColor} />
          {title}
        </div>
        {viewAllTo && (
          <Link to={viewAllTo} style={{ fontSize: 11, color: 'var(--accent-blue)' }}>
            View all →
          </Link>
        )}
      </div>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton" style={{ height: 28 }} />)}
        </div>
      ) : isEmpty ? (
        <Empty message={emptyMessage} />
      ) : (
        <div style={{ overflowX: 'auto' }}>{children}</div>
      )}
    </div>
  );
}

// ── RecentChangeLogsWidget ───────────────────────────────────────────────────
const ACTION_COLOR = {
  create:  'var(--accent-green)',
  update:  'var(--accent-blue)',
  delete:  'var(--accent-red)',
  restore: 'var(--accent-orange)',
};

export function RecentChangeLogsWidget({ filter = { limit: 10 } }) {
  const { data, loading } = useWidgetData('recent_change_logs', filter);
  const logs = data?.logs || data?.items || data?._array || (Array.isArray(data) ? data : []);

  return (
    <CardShell icon={History} iconColor="var(--accent-purple, #818cf8)" title="Audit Trail Terbaru"
      viewAllTo="/sync/change-log" loading={loading} isEmpty={logs.length === 0} emptyMessage="Belum ada activity log">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            {['Waktu', 'Resource', 'Action', 'Admin'].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {logs.map((row, i) => {
            const color = ACTION_COLOR[row.action] || 'var(--text-muted)';
            return (
              <tr key={row.id ?? i} style={{ borderTop: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{fmtDT(row.created_at)}</td>
                <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>
                  {row.resource_type || '—'}{row.resource_id != null ? ` #${row.resource_id}` : ''}
                </td>
                <td style={tdStyle}>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600, background: `${color}1A`, color }}>
                    {row.action || '—'}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{row.admin_username || 'system'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </CardShell>
  );
}

// ── RecentAuthLogsWidget (admin login activity) ──────────────────────────────
const EVENT_COLOR = {
  login_success: 'var(--accent-green)',
  login_failed:  'var(--accent-red)',
  token_refresh: 'var(--accent-blue)',
  logout:        'var(--text-muted)',
};

export function RecentAuthLogsWidget({ filter = { limit: 10 } }) {
  const { data, loading } = useWidgetData('recent_auth_logs', filter);
  const logs = data?.logs || data?.items || data?._array || (Array.isArray(data) ? data : []);

  return (
    <CardShell icon={ShieldAlert} iconColor="var(--accent-cyan)" title="Aktivitas Login Terbaru"
      viewAllTo="/sync/auth-log" loading={loading} isEmpty={logs.length === 0} emptyMessage="Belum ada activity login">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            {['Waktu', 'Username', 'Event', 'IP'].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {logs.map((row, i) => {
            const color = EVENT_COLOR[row.event_type] || 'var(--text-muted)';
            return (
              <tr key={row.id ?? i} style={{ borderTop: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{fmtDT(row.created_at)}</td>
                <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
                  {row.admin_username || row.attempted_username || '—'}
                </td>
                <td style={tdStyle}>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600, background: `${color}1A`, color }}>
                    {row.event_type || '—'}
                  </span>
                </td>
                <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{row.ip_address || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </CardShell>
  );
}

// ── RecentSyncLogsWidget ─────────────────────────────────────────────────────
export function RecentSyncLogsWidget({ filter = { limit: 10 } }) {
  const { data, loading } = useWidgetData('recent_sync_logs', filter);
  const logs = data?.logs || data?.items || data?._array || (Array.isArray(data) ? data : []);

  return (
    <CardShell icon={RefreshCw} iconColor="var(--accent-orange)" title="Sync Log Terbaru"
      viewAllTo="/sync/log" loading={loading} isEmpty={logs.length === 0} emptyMessage="Belum ada sync log">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            {['Waktu', 'Resource', 'Status', 'Message'].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {logs.map((row, i) => (
            <tr key={row.id ?? i} style={{ borderTop: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{fmtDT(row.created_at)}</td>
              <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>
                {row.resource_type || '—'}{row.resource_id != null ? ` #${row.resource_id}` : ''}
              </td>
              <td style={tdStyle}><Badge status={row.status} /></td>
              <td style={{ ...tdStyle, color: 'var(--text-muted)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.message || ''}>
                {row.message || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardShell>
  );
}